class SablewoodProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.currentOutput = this.querySelector('[data-gallery-current]');
    this.totalOutput = this.querySelector('[data-gallery-total]');
    this.liveRegion = this.querySelector('[data-gallery-live]');
    this.activeIndex = 0;
    this.scrollFrame = null;
    this.controller = new AbortController();

    if (!this.track || this.slides.length === 0) return;

    this.previousButton?.addEventListener('click', () => this.selectPosition(this.activePosition - 1, true));
    this.nextButton?.addEventListener('click', () => this.selectPosition(this.activePosition + 1, true));

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.selectGlobal(Number(thumbnail.dataset.galleryIndex), true);
      });
    });

    this.track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.selectPosition(this.activePosition + (event.key === 'ArrowRight' ? 1 : -1), true);
    });

    this.track.addEventListener(
      'touchstart',
      (event) => {
        if (
          !['dogstoppers', 'hamburger-large', 'puppy-can', 'squeak-ball'].includes(this.dataset.galleryVariantControl) ||
          event.touches.length !== 1
        ) return;
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchStartPosition = this.activePosition;
      },
      { passive: true }
    );

    this.track.addEventListener(
      'touchend',
      (event) => {
        if (
          !['dogstoppers', 'hamburger-large', 'puppy-can', 'squeak-ball'].includes(this.dataset.galleryVariantControl) ||
          event.changedTouches.length !== 1
        ) return;
        const deltaX = this.touchStartX - event.changedTouches[0].clientX;
        const deltaY = this.touchStartY - event.changedTouches[0].clientY;
        const total = this.visibleSlides.length;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY) || total < 2) return;
        if (deltaX > 0 && this.touchStartPosition === total - 1) {
          this.selectPosition(this.touchStartPosition + 1, true);
        } else if (deltaX < 0 && this.touchStartPosition === 0) {
          this.selectPosition(this.touchStartPosition - 1, true);
        }
      },
      { passive: true }
    );

    this.track.addEventListener(
      'scroll',
      () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const position = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
          const slide = this.visibleSlides[Math.max(0, Math.min(position, this.visibleSlides.length - 1))];
          if (slide) this.setActiveGlobal(Number(slide.dataset.galleryIndex), true);
        });
      },
      { passive: true }
    );

    const eventTarget = this.closest('.shopify-section, dialog') || document;
    eventTarget.addEventListener('shopify:product:select', this.handleProductSelect, {
      signal: this.controller.signal,
    });

    if (this.dataset.galleryVariantControl === 'animal-crunchers') {
      this.setVariantGroup(this.dataset.galleryInitialGroup || '3.5', null, false);
    } else if (this.usesColorFocus) {
      this.refreshVisibleItems();
      this.selectColor(this.dataset.galleryInitialColor || 'Smoke', false);
    } else {
      this.refreshVisibleItems();
      const initialMedia = this.visibleSlides.find(
        (slide) => String(slide.dataset.galleryMediaId) === String(this.dataset.galleryInitialMediaId)
      );
      const initialSlide = initialMedia || this.visibleSlides[0];
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.selectGlobal(Number(initialSlide?.dataset.galleryIndex || 0), false, false);
      });
    }
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ variant, detail }) => {
        const selectedMediaId = detail?.resource?.featured_media?.id;
        if (this.dataset.galleryVariantControl === 'animal-crunchers') {
          const size =
            variant?.selectedOptions?.find((option) => option.name === 'Size')?.value ||
            detail?.resource?.option1 ||
            detail?.resource?.options?.[0] ||
            '';
          this.setVariantGroup(size.includes('12') ? '12' : '3.5', selectedMediaId, true);
          return;
        }
        if (this.usesColorFocus) {
          const color =
            variant?.selectedOptions?.find((option) => option.name === 'Color')?.value ||
            detail?.resource?.option1 ||
            detail?.resource?.options?.[0] ||
            '';
          this.selectColor(color, true);
          return;
        }
        if (['brown-canvas', 'hamburger-large', 'puppy-can', 'squeak-ball'].includes(this.dataset.galleryVariantControl)) {
          this.selectMediaById(this.dataset.galleryInitialMediaId, true);
          return;
        }
        if (selectedMediaId) this.selectMediaById(selectedMediaId, true);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sablewood-product-gallery] Variant update failed:', error);
      });
  };

  get visibleSlides() {
    return this.slides.filter((slide) => !slide.hidden);
  }

  get visibleThumbnails() {
    return this.thumbnails.filter((thumbnail) => !thumbnail.hidden);
  }

  get usesColorFocus() {
    return ['smoke-sherpa', 'bear-y-turtleneck'].includes(this.dataset.galleryVariantControl);
  }

  get activePosition() {
    return Math.max(
      0,
      this.visibleSlides.findIndex((slide) => Number(slide.dataset.galleryIndex) === this.activeIndex)
    );
  }

  get wrapsNavigation() {
    return this.usesColorFocus || ['dogstoppers', 'brown-canvas', 'hamburger-large', 'puppy-can', 'squeak-ball'].includes(this.dataset.galleryVariantControl);
  }

  refreshVisibleItems() {
    const total = this.visibleSlides.length;
    if (this.totalOutput) this.totalOutput.textContent = String(total);

    this.visibleThumbnails.forEach((thumbnail, position) => {
      thumbnail.setAttribute('aria-label', `Show product image ${position + 1} of ${total}`);
    });

    const hasMultiple = total > 1;
    if (this.previousButton) this.previousButton.hidden = !hasMultiple;
    if (this.nextButton) this.nextButton.hidden = !hasMultiple;
  }

  setVariantGroup(group, preferredMediaId, announce) {
    this.slides.forEach((slide) => {
      slide.hidden = slide.dataset.galleryVariantGroup !== group;
    });
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.hidden = thumbnail.dataset.galleryVariantGroup !== group;
    });
    this.dataset.galleryActiveGroup = group;
    this.refreshVisibleItems();

    const preferred = this.visibleSlides.find(
      (slide) => String(slide.dataset.galleryMediaId) === String(preferredMediaId)
    );
    const target = preferred || this.visibleSlides[0];
    if (target) this.selectGlobal(Number(target.dataset.galleryIndex), announce, false);
  }

  selectMediaById(mediaId, announce) {
    const slide = this.visibleSlides.find((item) => String(item.dataset.galleryMediaId) === String(mediaId));
    if (slide) this.selectGlobal(Number(slide.dataset.galleryIndex), announce);
  }

  selectColor(color, announce) {
    const normalizedColor = String(color).trim().toLowerCase();
    this.updateSizeGuideContext(color);
    const slide = this.visibleSlides.find(
      (item) => String(item.dataset.galleryColor).trim().toLowerCase() === normalizedColor
    );
    if (slide) this.selectGlobal(Number(slide.dataset.galleryIndex), announce);
  }

  selectPosition(position, announce) {
    const total = this.visibleSlides.length;
    if (total === 0) return;
    const wraps = this.wrapsNavigation && total > 1;
    const nextPosition = wraps ? ((position % total) + total) % total : Math.max(0, Math.min(position, total - 1));
    const slide = this.visibleSlides[nextPosition];
    if (slide) this.selectGlobal(Number(slide.dataset.galleryIndex), announce);
  }

  updateSizeGuideContext(color) {
    const productComponent = this.closest('product-component');
    const status = productComponent?.querySelector('[data-smoke-size-status]');
    const contact = productComponent?.querySelector('[data-smoke-size-contact]');
    if (!status || !contact) return;

    const selectedColor = String(color).trim() || 'Selected color';
    const isSmoke = selectedColor.toLowerCase() === 'smoke';
    status.textContent = isSmoke
      ? 'Smoke measurements are verified.'
      : `Measurements for ${selectedColor} are not verified.`;
    contact.hidden = isSmoke;
  }

  selectGlobal(index, announce, smooth = true) {
    const slide = this.slides.find((item) => Number(item.dataset.galleryIndex) === index && !item.hidden);
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const trackBounds = this.track.getBoundingClientRect();
    const slideBounds = slide.getBoundingClientRect();
    const left = this.track.scrollLeft + slideBounds.left - trackBounds.left;

    this.track.scrollTo({
      left,
      behavior: !smooth || reduceMotion ? 'auto' : 'smooth',
    });
    this.setActiveGlobal(index, announce);
  }

  setActiveGlobal(index, announce) {
    const activeSlide = this.slides.find(
      (slide) => Number(slide.dataset.galleryIndex) === index && !slide.hidden
    );
    if (!activeSlide) return;
    const changed = index !== this.activeIndex;
    this.activeIndex = index;
    const position = this.activePosition;
    const total = this.visibleSlides.length;

    this.slides.forEach((slide) => {
      const isActive = !slide.hidden && Number(slide.dataset.galleryIndex) === index;
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);
    });

    this.thumbnails.forEach((thumbnail) => {
      const isActive = !thumbnail.hidden && Number(thumbnail.dataset.galleryIndex) === index;
      thumbnail.setAttribute('aria-current', String(isActive));
    });

    if (this.currentOutput) this.currentOutput.textContent = String(position + 1);
    if (this.totalOutput) this.totalOutput.textContent = String(total);
    const wraps = this.wrapsNavigation && total > 1;
    if (this.previousButton) this.previousButton.disabled = !wraps && position === 0;
    if (this.nextButton) this.nextButton.disabled = !wraps && position === total - 1;

    if (changed && announce && this.liveRegion) {
      this.liveRegion.textContent = `Product image ${position + 1} of ${total}`;
    }

    this.visibleThumbnails[position]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('sablewood-product-gallery')) {
  customElements.define('sablewood-product-gallery', SablewoodProductGallery);
}
